import React from 'react';
import PropTypes from 'prop-types';
import InnerHTML from '@Components/InnerHTML';
import { SUBSTANCE_DETAILS_TOOLTIPS } from '@Constants/ElementConstants';
import get from 'lodash/get';
import isArray from 'lodash/isArray';
import isEmpty from 'lodash/isEmpty';
import CasNumberContainer, { isCasNumberAllowed } from '@Containers/CasNumber';
import { Card } from '@els/els-react--card';
import { LinkElement } from '@els/els-react--link-element';
import Auth from '@Services/Auth';
import { FEATURES } from '@Constants/rx-ui/constants';
import { getCombinedRouteParams } from '@AppRoutes/AppRoutesSelectors';
import store from '@Store';
import { MetadataPropTypesShape } from '@Components/Results/MetadataPropTypes';
import SubstanceRightHandLinks from '@Components/Results/substances/details/SubstanceRightHandLinks';
import SubstancePubChemReferences from '@Components/Results/substances/details/substance-pubchem-references.component';
import ShoppingCartTooltipContent from '@Components/Results/structure/availability/ShoppingCartTooltipContent';

import './SubstanceDetailsContainer.scss';

function SubstanceHeaderMetaCard({ label, children, className = '' }) {
  return (
    <Card className={`substance-header-meta-card ${className}`}>
      <dl className="substance-header-meta-card__list">
        <dt className="substance-header-meta-card__label">{label}</dt>
        <dd className="substance-header-meta-card__value">{children}</dd>
      </dl>
    </Card>
  );
}

function SubstanceHeader({
  substance,
  database: { id: databaseId, name: databaseName, isCIDatabase, isCommercialSupplierData, isPubChem },
  metadata,
}) {
  const { sourceUrl, details = {}, molImage, similarityPercentage } = substance;
  const resultName = get(metadata, 'resultName');
  const chemicalNames = details?.chemicalNames ? details.chemicalNames : [];
  const name = isArray(chemicalNames) && chemicalNames.length > 0 ? chemicalNames[0] : `${databaseName} ID: ${details?.id || ''}`;
  const mapped = SUBSTANCE_DETAILS_TOOLTIPS[databaseId];
  const idLabel = get(mapped, 'id') || (databaseName === 'Reaxys' ? 'Reaxys Registry Number' : `${databaseName} ID`);
  const { sort } = getCombinedRouteParams(store.getState());
  const showCasCta = !Auth.isFeatureEnabled(FEATURES.disableCasNumberCta);
  const casRegistryNumbers = isArray(details?.casRegistryNumbers) ? details?.casRegistryNumbers?.join(', ') : details?.casRegistryNumbers;
  const formula = details?.linearFormula || details?.formula;
  const showSimilarity = sort === 'SIMILARITY' && molImage?.structure && similarityPercentage >= 0;

  return (
    <header className="substance-header">
      <InnerHTML mode="escapeCustom" tag="h4" className="substance__title u-els-font-size-h4 u-els-line-height-default" html={name} />
      {formula && <InnerHTML tag="span" className="substance-header__formula" html={formula} mode="escape" />}
      <div className="substance__meta substance-header__meta" aria-label="Substance identifiers">
        {details?.weight && (
          <SubstanceHeaderMetaCard label="Molecular Weight:">
            <InnerHTML tag="span" html={details.weight} />
          </SubstanceHeaderMetaCard>
        )}
        {details?.id && !isCIDatabase && (
          <SubstanceHeaderMetaCard label={idLabel}>
            {sourceUrl ? (
              <LinkElement data-e2e="substance-meta-item-link" href={sourceUrl} isExternal>
                <InnerHTML tag="span" html={details.id} />
              </LinkElement>
            ) : (
              <InnerHTML tag="span" html={details.id} />
            )}
          </SubstanceHeaderMetaCard>
        )}
        {showCasCta ? (
          <SubstanceHeaderMetaCard label="CAS Registry Number:">
            <CasNumberContainer
              className="substance__meta__item"
              casRegistryNumbers={details?.casRegistryNumbers}
              substanceId={details?.id}
              databaseId={databaseId}
              resultName={resultName}
              name={details?.chemicalNames && details?.chemicalNames[0]}
              structure={molImage?.structure}
              inChiKey={details?.inChiKey}
              withCta
              showTooltip={false}
            />
          </SubstanceHeaderMetaCard>
        ) : (
          isCasNumberAllowed(databaseId) &&
          !isEmpty(details?.casRegistryNumbers) && (
            <SubstanceHeaderMetaCard label="CAS Registry Number:">
              <InnerHTML tag="span" html={casRegistryNumbers} />
            </SubstanceHeaderMetaCard>
          )
        )}
        {showSimilarity && (
          <SubstanceHeaderMetaCard label="Similarity" className="substance-header-meta-card--similarity">
            <span>{similarityPercentage}%</span>
          </SubstanceHeaderMetaCard>
        )}
      </div>
      {!metadata?.heatmapMode && isCommercialSupplierData && (
        <ShoppingCartTooltipContent details={details} className="substance-header__commerce-summary" packageLabel="Packages available:" />
      )}
      {!metadata?.heatmapMode && isPubChem && (
        <SubstancePubChemReferences
          references={substance.pubchemReferences}
          className="substance-header__pubchem-links substance-header__pubchem-links--in-header"
        />
      )}
      {!metadata?.heatmapMode && (
        <SubstanceRightHandLinks
          metadata={metadata}
          substance={substance}
          className="substance-header__related-links substance-header__related-links--in-header"
        />
      )}
    </header>
  );
}

SubstanceHeaderMetaCard.propTypes = {
  label: PropTypes.string.isRequired,
  children: PropTypes.node.isRequired,
  className: PropTypes.string,
};

SubstanceHeader.propTypes = {
  metadata: MetadataPropTypesShape,
  substance: PropTypes.object.isRequired,
  database: PropTypes.object.isRequired,
};

export default SubstanceHeader;
